interface ModalPosition {
  top: number;
  left?: number;
  right?: number;
  centered?: boolean;
}

interface ModalDimensions {
  width: number;
  height: number;
}

/**
 * Calculate optimal modal position based on mouse event and modal dimensions
 * Handles mobile vs desktop positioning and viewport overflow prevention
 */
export const useModalPosition = () => {
  const calculatePosition = (
    mouseEvent: MouseEvent,
    modalDimensions: ModalDimensions,
    isMobile = window.innerWidth < 768
  ): ModalPosition => {
    // On mobile, always center the modal
    if (isMobile) {
      return { top: window.scrollY + 100, centered: true };
    }

    // Desktop positioning
    const { width: modalWidth, height: modalHeight } = modalDimensions;
    const padding = 20;

    let top = mouseEvent.clientY + window.scrollY + padding;
    let left = mouseEvent.clientX + padding;

    // Check if popup would overflow viewport horizontally
    if (left + modalWidth > window.innerWidth) {
      left = mouseEvent.clientX - modalWidth - padding;
    }

    // Check if popup would overflow viewport vertically
    if (top + modalHeight > window.innerHeight + window.scrollY) {
      top = mouseEvent.clientY + window.scrollY - modalHeight - padding;
    }

    return { top, left };
  };

  const calculatePositionForElement = (
    element: HTMLElement,
    modalDimensions: ModalDimensions,
    isMobile = window.innerWidth < 768
  ): ModalPosition => {
    // On mobile, always center
    if (isMobile) {
      return { top: window.scrollY + 100, centered: true };
    }

    const rect = element.getBoundingClientRect();
    const { width: modalWidth, height: modalHeight } = modalDimensions;
    const padding = 20;

    // Calculate available space on left and right
    const spaceOnRight = window.innerWidth - rect.right;
    const spaceOnLeft = rect.left;

    // Determine if we have enough space on either side horizontally
    const hasSpaceRight = spaceOnRight >= modalWidth + padding;
    const hasSpaceLeft = spaceOnLeft >= modalWidth + padding;

    // Determine vertical positioning
    let verticalTop: number;

    // Start by trying to align top of modal with top of event
    let idealTop = rect.top + window.scrollY;

    // Check if modal would overflow bottom of viewport
    const modalBottom = idealTop + modalHeight;
    const viewportBottom = window.scrollY + window.innerHeight;

    if (modalBottom > viewportBottom) {
      // Modal would overflow - adjust to align bottom of modal with bottom of event
      // But make sure we don't go above the viewport
      verticalTop = Math.max(
        window.scrollY + padding, // Don't go above viewport with padding
        rect.bottom + window.scrollY - modalHeight // Align bottom with event
      );
    } else {
      // No overflow - use ideal position
      verticalTop = idealTop;
    }

    let position: ModalPosition;

    if (hasSpaceRight) {
      // Position on the right side of the event
      position = {
        top: verticalTop,
        left: rect.right + padding
      };
    } else if (hasSpaceLeft) {
      // Position on the left side of the event
      position = {
        top: verticalTop,
        right: window.innerWidth - rect.left + padding
      };
    } else {
      // Center if no space on either side
      position = {
        top: verticalTop,
        centered: true
      };
    }

    return position;
  };

  return {
    calculatePosition,
    calculatePositionForElement
  };
};
